import { useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

function usePopupPosition(open, wrapRef) {
  const [style, setStyle] = useState(null);

  const updatePosition = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap || !open) return;
    const rect = wrap.getBoundingClientRect();
    const gap = 4;
    const maxH = 220;
    let top = rect.bottom + gap;
    if (top + maxH > window.innerHeight - gap) {
      const above = rect.top - gap - maxH;
      top = above >= gap ? above : Math.max(gap, window.innerHeight - maxH - gap);
    }
    setStyle({
      position: 'fixed',
      top,
      left: rect.left,
      width: rect.width,
      maxHeight: maxH,
      zIndex: 2000,
    });
  }, [open, wrapRef]);

  useLayoutEffect(() => {
    if (!open) { setStyle(null); return undefined; }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  return style;
}

/**
 * Searchable combobox — type to filter, click option to select.
 * options: [{ value, label, meta? }]
 */
export default function SearchCombobox({
  label,
  placeholder = 'جستجو...',
  options = [],
  value,
  onChange,
  onSelect,
  onSearchChange,
  loading = false,
  emptyText = 'موردی یافت نشد',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const popupStyle = usePopupPosition(open && !disabled, wrapRef);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value],
  );

  useEffect(() => {
    if (selected) {
      setQuery(selected.label);
    }
  }, [selected?.value, selected?.label]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 80);
    return options.filter((o) => {
      const hay = `${o.label || ''} ${o.meta || ''} ${o.value || ''}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 80);
  }, [options, query]);

  const pick = (opt) => {
    onChange?.(opt.value);
    onSelect?.(opt);
    setQuery(opt.label);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginBottom: 12 }}>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
          {label}
        </label>
      )}
      <input
        type="text"
        value={query}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          onSearchChange?.(e.target.value);
          if (!e.target.value.trim()) onChange?.('');
        }}
        style={{
          width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8,
          fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', direction: 'rtl',
          background: disabled ? '#f8fafc' : '#fff',
        }}
      />
      {open && !disabled && popupStyle && createPortal(
        <div style={{
          ...popupStyle, overflowY: 'auto',
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
        }}
        >
          {loading ? (
            <div style={{ padding: '12px 14px', fontSize: 12, color: '#64748b' }}>در حال بارگذاری...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 12, color: '#94a3b8' }}>{emptyText}</div>
          ) : (
            filtered.map((opt) => (
              <button
                key={`${opt.value}-${opt.label}`}
                type="button"
                onClick={() => pick(opt)}
                style={{
                  display: 'block', width: '100%', textAlign: 'right', padding: '10px 14px',
                  border: 'none', borderBottom: '1px solid #f1f5f9', background: String(opt.value) === String(value) ? '#EDF3FC' : '#fff',
                  cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, color: '#0f172a',
                }}
              >
                <div style={{ fontWeight: 600 }}>{opt.label}</div>
                {opt.meta && (
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{opt.meta}</div>
                )}
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
